// orderSearch.js
// -----------------------------------------------------------------------------
// The rules behind ค้นหาการจอง: what a typed query is, whether a stored phone
// field holds a number, how rows from bookingSlot / booking / deliveryList /
// deliveryStop become one order each, and what status that order is in.
//
// Pure: no Firestore, no Vue, no axios -- searchService.js does the reading and
// scripts/check-order-search.js requires this file directly. CommonJS, like
// bookingRules.js and driverDay.js, which it builds on.
//
// See docs/superpowers/specs/2026-09-21-order-search-design.md.
// -----------------------------------------------------------------------------

const R = require('./bookingRules')
const D = require('./driverDay')
const P = require('./people')
const X = require('./proposalRules')
const U = require('./upcountry')
const RJ = require('./returnJob')

// Nine digits is a Thai landline with its area code; ten a mobile. Anything
// shorter is a fragment, and a fragment is refused rather than matched partly.
const MIN_TEL_DIGITS = 9
const HISTORY_DAYS = 30

// Every phone begins 0 or + (+66, or a foreign country code); no SO number
// does. That, not length, is what tells them apart -- SO numbers are eight or
// nine digits, like a landline. Only the first number counts, so a number read
// out with its extension ("081-234-5678 ต่อ 12") is still a phone, and it is
// the same first number R.normalizeTel stored as telDigits.
function classifyQuery (text) {
  const raw = text == null ? '' : String(text).trim()
  if (!raw) return { kind: 'empty' }
  const compact = raw.replace(/[\s\-().]/g, '')
  if (/^(\+|0)\d*$/.test(R.firstTelPart(raw).replace(/[\s\-().]/g, ''))) {
    const digits = R.normalizeTel(raw)
    return digits.length >= MIN_TEL_DIGITS ? { kind: 'tel', digits: digits } : { kind: 'short' }
  }
  return { kind: 'so', soNo: compact }
}

// A stored phone field is whatever SAP held: "081-234567 ต่อ 12",
// "081-234567,081234567", "02-1234567 หรือ 081...", or "******12". Commas,
// slashes, semicolons and any run of Thai text separate numbers; a segment
// with too few digits (an extension, a masked number) is not a number.
function telNumbers (stored) {
  if (stored == null) return []
  return String(stored)
    .split(/[,;/]|[฀-๿]+/)
    .map(function (part) { return R.normalizeTel(part) })
    .filter(function (d) { return d.length >= MIN_TEL_DIGITS })
}

function telMatches (stored, digits) {
  return !!digits && telNumbers(stored).indexOf(digits) !== -1
}

// The forms a number was found written in, in `deliveryList.custTel` and
// `booking.telephone`, surveyed on 2026-09-21 (5,052 deliveryList rows since
// 2026-06-01): plain digits (76%), 3-6 (9%), then 2-7, 3-3-3, 3-6-1, 3-3-4 and
// "3- 3-3". searchService runs each as a PREFIX range query, so a number
// followed by an extension or a second number is still found; telMatches then
// decides. A number written second in a field is not found -- about 1% of rows.
function telPrefixes (digits) {
  const d = String(digits || '')
  if (d.length === 10) {
    return [d,
      d.slice(0, 3) + '-' + d.slice(3),
      d.slice(0, 3) + '-' + d.slice(3, 6) + '-' + d.slice(6),
      d.slice(0, 3) + '-' + d.slice(3, 9) + '-' + d.slice(9)]
  }
  if (d.length === 9) {
    return [d,
      d.slice(0, 3) + '-' + d.slice(3),
      d.slice(0, 2) + '-' + d.slice(2),
      d.slice(0, 3) + '-' + d.slice(3, 6) + '-' + d.slice(6),
      d.slice(0, 3) + '- ' + d.slice(3, 6) + '-' + d.slice(6),
      d.slice(0, 2) + '-' + d.slice(2, 5) + '-' + d.slice(5)]
  }
  return d ? [d] : []
}

const STATUS_LABEL = {
  unbooked: 'ยังไม่ได้จองวันส่ง',
  sapCancelled: 'SO ถูกยกเลิกใน SAP',
  sapClosed: 'SO ถูกปิดใน SAP',
  sapDispatched: 'อยู่ในใบจัดรถแล้ว',
  booked: 'จองวันส่งแล้ว',
  upPool: 'รอจัดรอบรถใหญ่',
  upTrip: 'จัดรอบรถใหญ่แล้ว',
  planned: 'วางแผนรถแล้ว',
  dispatched: 'จัดรถแล้ว',
  enroute: 'กำลังเดินทาง',
  arrived: 'ถึงบ้านลูกค้า',
  installing: 'กำลังติดตั้ง',
  done: 'ส่งเสร็จ',
  failed: 'ส่งไม่สำเร็จ',
  cancelled: 'ยกเลิก'
}

// driverDay's status names, read through driverDay.statusFor so this page can
// never disagree with the driver app or the monitor board. `pending` is a job
// on a truck that has not left yet.
const DRIVER_STATUS = {
  pending: 'dispatched',
  enroute: 'enroute',
  arrived: 'arrived',
  installing: 'installing',
  done: 'done',
  failed: 'failed'
}

function soOf (doc) {
  const s = doc && doc.soNo
  return s == null ? '' : String(s).trim()
}

// The key a document groups under: its SO, or for a return-only pickup its RT.
// Prefixed, because an RT number and an SO number can be the same digits.
function keyOf (doc) {
  const so = soOf(doc)
  if (so) return so
  const rt = doc && doc.rtNo != null ? String(doc.rtNo).trim() : ''
  return rt ? 'rt:' + rt : ''
}

function text (v) {
  return v == null ? '' : String(v).trim()
}

function firstText (values) {
  for (let i = 0; i < values.length; i++) {
    const t = text(values[i])
    if (t) return t
  }
  return ''
}

function latestBy (docs, field) {
  if (!docs.length) return null
  return docs.slice().sort(function (a, b) {
    return String(b[field] || '').localeCompare(String(a[field] || ''))
  })[0]
}

// An SO may have been booked, cancelled and booked again. The live booking is
// the answer; only when every one is cancelled is the latest cancellation.
function pickSlot (slots) {
  const live = slots.filter(function (s) { return R.isActive(s.status) })
  return latestBy(live.length ? live : slots, 'date')
}

// SAP lines when a stage stored them; the free text a booking was typed with
// when it did not. A text line has no separate quantity.
function productsOf (doc) {
  if (!doc) return []
  if (Array.isArray(doc.prodList) && doc.prodList.length) {
    return D.productsFor(doc.prodList).map(function (p) { return { name: p.name, qty: p.qty } })
  }
  return String(doc.productList || '').split('\n')
    .map(function (l) { return l.trim() })
    .filter(Boolean)
    .map(function (l) { return { name: l, qty: null } })
}

function firstProducts (docs) {
  for (let i = 0; i < docs.length; i++) {
    const p = productsOf(docs[i])
    if (p.length) return p
  }
  return []
}

// The phone's own clock for each driver step, as HH:mm Bangkok; '' when the
// step is unstamped. `atDevice` rather than the server `at`, because only the
// ISO string can be read without Firestore.
function stepClocks (steps) {
  const out = {}
  D.STEPS.concat([D.REJECTED]).forEach(function (step) {
    const s = steps && steps[step]
    out[step] = s && s.atDevice ? D.clockFromISO(s.atDevice) : ''
  })
  return out
}

function firstNonEmpty (lists) {
  return (lists || []).find(l => Array.isArray(l) && l.length) || []
}

function buildOrder (g, stops) {
  const slot = pickSlot(g.slots)
  const booking = latestBy(g.bookings, 'date')
  const list = latestBy(g.lists, 'deliveryDate')
  const stop = list ? stops[D.stopIdFor(list.deliveryDate, list.regNum, D.stopKeyFor(list))] || null : null
  const steps = (stop && stop.steps) || {}

  let status
  if (list) {
    if (stop) {
      const driverStatus = DRIVER_STATUS[D.statusFor(steps)]
      // A driver may finish a job on the legacy /driver/timestamp screen after
      // starting it in the new app; that outranks an open driver-app status,
      // but never one already closed (done or failed said their own way).
      status = (!D.isClosed(driverStatus) && list.timeStampEnd) ? 'done' : driverStatus
    } else {
      status = list.timeStampEnd ? 'done' : 'dispatched'
    }
  } else if (booking) status = 'planned'
  else if (slot && R.isActive(slot.status)) {
    // An up-country order has no day until a trip is fixed; say which stage.
    status = U.isUpcountry(slot) ? (slot.upTripId ? 'upTrip' : 'upPool') : 'booked'
  }
  else status = 'cancelled'

  // Furthest stage first: dispatch may have moved the date, and deliveryList
  // is what the truck will actually do.
  let stage = list ? 'list' : booking ? 'booking' : 'slot'

  // A ส่งไม่สำเร็จ job is rebooked by hand -- the failed deliveryList row
  // stays, but a live booking or bookingSlot dated after it is the real next
  // attempt. Dispatch may also move a delivery EARLIER than its booking, so
  // only a LATER live stage overrides -- a "latest date wins" rule was
  // rejected for exactly that case.
  let rebook = null
  if (status === 'failed') {
    const bookingLater = booking && R.isActive(booking.status) && text(booking.date) > list.deliveryDate
    const slotLater = slot && R.isActive(slot.status) && text(slot.date) > list.deliveryDate
    if (bookingLater) { stage = 'booking'; status = 'planned'; rebook = { date: text(booking.date), slot: text(booking.slot) } }
    else if (slotLater) { stage = 'slot'; status = 'booked'; rebook = { date: text(slot.date), slot: text(slot.slot) } }
  }

  const L = list || {}
  const B = booking || {}
  const S = slot || {}
  const proof = (stop && stop.proof) || {}

  return {
    key: g.key,
    stage: stage,
    status: status,
    label: STATUS_LABEL[status],
    soNo: firstText([soOf(list), soOf(booking), soOf(slot)]),
    rtNo: firstText([slot, booking, list].map(d => d && d.rtNo)),
    returnProducts: RJ.returnLines(firstNonEmpty([slot, booking, list].map(d => d && d.prodListRt))),
    date: rebook ? rebook.date : firstText([L.deliveryDate, B.date, S.date]),
    slot: rebook ? rebook.slot : firstText([B.slot, S.slot]),
    time: rebook ? '' : text(L.time),
    custName: firstText([L.custName, B.name, S.custName, S.name]),
    tel: firstText([L.custTel, B.telephone, S.telephone]),
    address: firstText([L.custAddress, B.address, S.address, S.custAddress]),
    landmark: firstText([L.landmark, B.landmark, S.landmark]),
    geo: firstText([L.geo, B.geo, S.geo]),
    products: firstProducts([list, booking, slot]),
    owner: firstText([S.user, B.user]),
    // The order number in the system that handed this booking over prefilled
    // (bookingDate's ?appt=1&ref=). Only a bookingSlot carries it -- nothing
    // copies it onward -- so a dispatched job still shows it from its slot.
    sourceRef: text(S.sourceRef),
    // Up-country (large truck): its groups and earliest week, null otherwise.
    upcountry: slot && U.isUpcountry(slot) ? {
      groups: slot.upGroup || [],
      groupLabel: (slot.upGroup || []).map(U.groupLabel).join(' / '),
      week: text(slot.earliestWeek),
      regNum: text(slot.upRegNum)
    } : null,
    slotId: slot ? slot.id : text(B.sourceSlotId),
    // The live slot's own SO when it has one, else its phone -- what a slot
    // holds when ใส่ SO on carBooking set the SO on its booking alone (see
    // mergeOrders), and so what bookingDate's search must be given instead.
    manageKey: slot ? (soOf(slot) || R.normalizeTel(slot.telephone)) : '',
    regNum: rebook ? '' : text(L.regNum),
    driverName: rebook ? '' : text(L.driverName),
    clocks: rebook ? stepClocks({}) : stepClocks(steps),
    rejectReasons: status === 'failed' ? (proof.rejectReasons || []) : [],
    rejectNote: status === 'failed' ? text(proof.rejectNote) : '',
    cancelled: status === 'cancelled' ? { by: text(S.cancelledBy), at: text(S.cancelledAt) } : null,
    // A request already made about this order, so a second caller hears the
    // same answer instead of stacking another (propose and confirm).
    // What bookingHistory.timeline reads: the live slot's own record of who
    // booked, moved, cancelled or asked to. Null for a job with no bookingSlot.
    historySource: slot ? {
      createdAt: slot.createdAt,
      user: slot.user,
      date: slot.date,
      slot: slot.slot,
      fleet: slot.fleet || null,
      earliestWeek: slot.earliestWeek || null,
      history: slot.history || [],
      proposalHistory: slot.proposalHistory || [],
      proposal: slot.proposal || null,
      proposalStatus: slot.proposalStatus || null
    } : null,
    pendingProposal: (slot && slot.proposalStatus === 'pending' && slot.proposal)
      ? { kind: slot.proposal.kind, date: text(slot.proposal.date), slot: text(slot.proposal.slot), by: text(slot.proposal.by) }
      : null
  }
}

// One order per SO. A นัดหมาย with no SO yet is keyed by its bookingSlot id,
// and a planned `booking` finds that same key through sourceSlotId -- so a
// booking the planner copied before its SO arrived still joins its slot.
function mergeOrders (input) {
  const i = input || {}
  const groups = {}
  const keys = []
  function group (key) {
    if (!groups[key]) { groups[key] = { key: key, slots: [], bookings: [], lists: [] }; keys.push(key) }
    return groups[key]
  }
  // ใส่ SO on carBooking sets soNo on the booking alone, never its
  // bookingSlot. Before keying slots, learn which SO such a booking carries
  // for its source slot, so that slot -- which still has no SO of its own --
  // joins the same order instead of keeping its old slot:<id> key.
  const soBySlotId = {}
  ;(i.bookings || []).forEach(function (b) {
    if (soOf(b) && b.sourceSlotId) soBySlotId[b.sourceSlotId] = soOf(b)
  })
  const slotKey = {}
  ;(i.slots || []).forEach(function (s) {
    const key = keyOf(s) || soBySlotId[s.id] || 'slot:' + s.id
    slotKey[s.id] = key
    group(key).slots.push(s)
  })
  ;(i.bookings || []).forEach(function (b) {
    const viaSlot = b.sourceSlotId ? (slotKey[b.sourceSlotId] || 'slot:' + b.sourceSlotId) : ''
    group(keyOf(b) || viaSlot || 'booking:' + b.id).bookings.push(b)
  })
  ;(i.lists || []).forEach(function (l) {
    group(keyOf(l) || 'list:' + l.id).lists.push(l)
  })
  return sortOrders(keys.map(function (k) { return buildOrder(groups[k], i.stops || {}) }))
}

// Newest delivery first; an order with no date (SAP only) at the top, since
// it is the answer to the question just asked.
function sortOrders (orders) {
  return orders.slice().sort(function (a, b) {
    if (!a.date !== !b.date) return a.date ? 1 : -1
    return String(b.date).localeCompare(String(a.date)) || String(a.key).localeCompare(String(b.key))
  })
}

function daysBefore (ymd, n) {
  const d = new Date(ymd + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() - n)
  return d.toISOString().slice(0, 10)
}

// Upcoming and the last 30 days open; the rest folds under ประวัติเก่า.
function splitHistory (orders, today) {
  const cutoff = daysBefore(today, HISTORY_DAYS)
  const recent = []
  const older = []
  orders.forEach(function (o) {
    if (!o.date || o.date >= cutoff) recent.push(o)
    else older.push(o)
  })
  return { recent: recent, older: older }
}

// จัดการใน จองวันส่งสินค้า is offered only where that page can act: a live
// booking made there, owned by this user or any for staff. A job added straight
// into carBooking has no bookingSlot and is managed on carBooking.
function canManage (order, role, identities) {
  if (!order || !order.slotId || order.status === 'cancelled') return false
  if (R.isStaffRole(role)) return true
  return P.ownedBy(order.owner, identities)
}

// The one action an order card offers (propose and confirm):
//   'pending'    a request is already waiting -- shown, never stacked
//   'manage'     the owner or staff: จัดการ on จองวันส่งสินค้า, as before
//   'dispatched' on a truck already, and not this caller's to manage -- sent
//                to delivery; an owner or staff still gets 'manage', checked
//                first, so a dispatched job stays exactly as manageable as it
//                always was for them
//   'propose'    another shop: ขอเลื่อน / ขอยกเลิก
//   'none'       nothing to act on (cancelled, no bookingSlot, a driver)
function actionFor (order, role, identities) {
  if (!order || role === 'driver') return 'none'
  if (order.pendingProposal) return 'pending'
  if (!order.slotId || order.status === 'cancelled') return 'none'
  if (canManage(order, role, identities)) return 'manage'
  if (order.stage === 'list') return 'dispatched'
  return 'propose'
}

// ใส่ SO on the card, beside whatever actionFor offers: an order still waiting
// for its SO (none anywhere -- not on its slot, not on a planned booking), with
// a live bookingSlot this caller may attach to. Who may is X.canAttachSo's
// answer -- the owner, staff, or a supervisor of the owner's group -- so this
// card and bookingDate's button cannot disagree. `ctx` is
// { role, identities, user, profiles }.
function canAttachSo (order, ctx) {
  if (!order || !order.slotId || order.soNo || order.status === 'cancelled') return false
  return X.canAttachSo({ status: 'active', soNo: '', rtNo: order.rtNo || '', user: order.owner }, ctx)
}

// What to put in bookingDate's search box. Not order.soNo: a slot split from
// its SO by ใส่ SO (see mergeOrders) has none of its own, and bookingDate
// searches bookingSlot -- so this is order.manageKey, what the slot itself
// holds.
function manageQuery (order) {
  return order.manageKey
}

// An SO in no collection, as GetSo returned it. Only `unbooked` offers จองวันส่ง.
function sapOrder (so, prodRows) {
  const s = so || {}
  let status = 'unbooked'
  if (s.canceled === 'Y') status = 'sapCancelled'
  else if (s.status === 'C') status = 'sapClosed'
  else if (s.transNo) status = 'sapDispatched'
  const addr = R.soAddressFields(s)
  return {
    key: 'sap:' + soOf(s),
    stage: 'sap',
    status: status,
    label: STATUS_LABEL[status],
    soNo: soOf(s),
    date: '',
    slot: '',
    time: '',
    custName: text(s.custName),
    tel: text(s.custTel),
    address: text(addr.address),
    landmark: text(addr.landmark),
    geo: text(addr.geo),
    products: D.productsFor(prodRows).map(function (p) { return { name: p.name, qty: p.qty } }),
    owner: '',
    slotId: '',
    manageKey: '',
    regNum: '',
    driverName: '',
    clocks: stepClocks({}),
    rejectReasons: [],
    rejectNote: '',
    cancelled: null,
    transNo: text(s.transNo)
  }
}

module.exports = {
  MIN_TEL_DIGITS, HISTORY_DAYS, STATUS_LABEL,
  classifyQuery, telNumbers, telMatches, telPrefixes,
  mergeOrders, sortOrders, splitHistory, canManage, actionFor, canAttachSo, manageQuery, sapOrder
}
